import { Link } from "react-router-dom";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, ExternalLink } from "lucide-react";
import useAutoScrollIntoView from "../../hooks/useAutoScrollIntoView";
import useAutoHide from "../../hooks/useAutoHide";
import MessageCountdown from "../common/MessageCountdown";

const VARIANTS = {
  error: {
    wrap: "border-red-200 bg-gradient-to-br from-red-50 via-white to-white shadow-sm shadow-red-100/40",
    accent: "bg-red-500",
    badge: "bg-red-100 text-red-800 ring-red-200",
    badgeLabel: "Error",
    iconWrap: "bg-red-100 text-red-700 ring-red-200",
    title: "text-red-950",
    message: "text-slate-800",
    hint: "text-slate-600",
    stepsWrap: "bg-white border-red-100",
    stepsTitle: "text-red-900",
    stepNum: "bg-red-100 text-red-800",
    stepText: "text-slate-700",
    stepLink: "text-indigo-700 hover:text-indigo-900",
  },
  warning: {
    wrap: "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white shadow-sm shadow-amber-100/40",
    accent: "bg-amber-500",
    badge: "bg-amber-100 text-amber-900 ring-amber-200",
    badgeLabel: "Warning",
    iconWrap: "bg-amber-100 text-amber-800 ring-amber-200",
    title: "text-amber-950",
    message: "text-slate-800",
    hint: "text-slate-600",
    stepsWrap: "bg-white border-amber-100",
    stepsTitle: "text-amber-900",
    stepNum: "bg-amber-100 text-amber-900",
    stepText: "text-slate-700",
    stepLink: "text-indigo-700 hover:text-indigo-900",
  },
  info: {
    wrap: "border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white shadow-sm shadow-blue-100/40",
    accent: "bg-blue-500",
    badge: "bg-blue-100 text-blue-800 ring-blue-200",
    badgeLabel: "Info",
    iconWrap: "bg-blue-100 text-blue-700 ring-blue-200",
    title: "text-blue-950",
    message: "text-slate-800",
    hint: "text-slate-600",
    stepsWrap: "bg-white border-blue-100",
    stepsTitle: "text-blue-900",
    stepNum: "bg-blue-100 text-blue-800",
    stepText: "text-slate-700",
    stepLink: "text-indigo-700 hover:text-indigo-900",
  },
  success: {
    wrap: "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white shadow-sm shadow-emerald-100/40",
    accent: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    badgeLabel: "Success",
    iconWrap: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    title: "text-emerald-950",
    message: "text-slate-800",
    hint: "text-slate-600",
    stepsWrap: "bg-white border-emerald-100",
    stepsTitle: "text-emerald-900",
    stepNum: "bg-emerald-100 text-emerald-800",
    stepText: "text-slate-700",
    stepLink: "text-indigo-700 hover:text-indigo-900",
  },
};

const ICONS = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};

function normalizeStep(step) {
  if (!step) return null;
  if (typeof step === "string") return { text: step };
  if (typeof step === "object" && step.text) {
    return { text: step.text, to: step.to || null, linkLabel: step.linkLabel || null };
  }
  return null;
}

/**
 * @param {object} props
 * @param {"error"|"warning"|"info"|"success"} [props.variant]
 * @param {string} [props.title]
 * @param {string} [props.message]
 * @param {string} [props.hint]
 * @param {(string|{text:string,to?:string,linkLabel?:string})[]} [props.steps]
 * @param {string} [props.stepsTitle]
 * @param {{label:string,to:string,primary?:boolean|string}[]} [props.guideLinks]
 * @param {import("react").ReactNode} [props.actions]
 * @param {() => void} [props.onDismiss]
 */
export default function NoticeCard({
  variant = "error",
  title,
  message,
  hint,
  steps,
  stepsTitle = "How to fix this",
  guideLinks,
  actions,
  onDismiss,
  badgeLabel,
  autoScroll = true,
}) {
  const scrollSignature = `${variant}|${title}|${message}|${hint || ""}`;
  const scrollRef = useAutoScrollIntoView(scrollSignature, {
    enabled: autoScroll && Boolean(message || title),
  });

  // Every notice closes 30s after it appears; a new or re-raised message shows again.
  const { visible, secondsLeft, close } = useAutoHide(message || title ? scrollSignature : "");

  if ((!message && !title) || !visible) return null;

  const v = VARIANTS[variant] || VARIANTS.error;
  const Icon = ICONS[variant] || AlertTriangle;
  const stepList = (Array.isArray(steps) ? steps : []).map(normalizeStep).filter(Boolean);
  const label = badgeLabel || v.badgeLabel;
  const links = Array.isArray(guideLinks)
    ? guideLinks.filter((l) => l?.label && l?.to)
    : [];

  const btnBase =
    "inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors";

  return (
    <div
      ref={scrollRef}
      data-auto-scroll-alert={autoScroll ? "true" : "false"}
      role={variant === "error" ? "alert" : "status"}
      className={`relative overflow-hidden rounded-2xl border outline-none scroll-mt-6 ${v.wrap}`}
      aria-live={variant === "error" ? "assertive" : "polite"}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${v.accent}`} aria-hidden />

      <div className="flex items-start gap-4 p-5 pl-6">
        <span
          className={`mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ${v.iconWrap}`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </span>

        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${v.badge}`}
              >
                {label}
              </span>
            </div>
            {title ? (
              <p className={`text-base font-semibold leading-snug ${v.title}`}>{title}</p>
            ) : null}
            {message ? (
              <p className={`mt-1.5 text-sm leading-relaxed whitespace-pre-line ${v.message}`}>{message}</p>
            ) : null}
            {hint ? (
              <p className={`mt-2 text-sm leading-relaxed ${v.hint}`}>{hint}</p>
            ) : null}
          </div>

          {stepList.length > 0 && (
            <div className={`rounded-xl border px-4 py-3 ${v.stepsWrap}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${v.stepsTitle}`}>
                {stepsTitle}
              </p>
              <ol className="mt-2.5 space-y-2.5">
                {stepList.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${v.stepNum}`}
                    >
                      {i + 1}
                    </span>
                    <span className={`leading-relaxed ${v.stepText}`}>
                      {step.text}
                      {step.to ? (
                        <>
                          {" "}
                          <Link
                            to={step.to}
                            className={`inline-flex items-center gap-0.5 font-semibold underline underline-offset-2 ${v.stepLink}`}
                          >
                            {step.linkLabel || "Open page"}
                            <ExternalLink className="h-3 w-3" aria-hidden />
                          </Link>
                        </>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {links.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              {links.map((l) => {
                const primary = l.primary === true || l.primary === "1" || l.primary === 1;
                return (
                  <Link
                    key={`${l.to}-${l.label}`}
                    to={l.to}
                    className={
                      primary
                        ? `${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`
                        : `${btnBase} border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50`
                    }
                  >
                    {l.label}
                    <ExternalLink className="h-3.5 w-3.5 opacity-80" aria-hidden />
                  </Link>
                );
              })}
            </div>
          ) : null}

          {actions ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">{actions}</div>
          ) : null}
        </div>

        <MessageCountdown
          seconds={secondsLeft}
          onClose={() => {
            close();
            onDismiss?.();
          }}
        />
      </div>
    </div>
  );
}
